import { composeThemeFromProps, Compose } from '@css-modules-theme/react';
import clsx from 'clsx';
import { forwardRef, useRef, useContext } from 'react';
import mergeRefs from 'react-merge-refs';
import { useUncontrolled } from '../../hooks/useUncontrolled.js';
import { RadioGroupContext } from './context/RadioGroup.context.js';
import defaultStyles from './styles/Radio.module.scss.js';
import { useRadio } from './utils/useRadio.js';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { Typography } from '../typography/Typography.js';
import { omit } from '../../utils/omit.js';
import { isFunction } from '../../utils/is.js';

const Radio = /*#__PURE__*/forwardRef((props, ref) => {
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);
  const context = useContext(RadioGroupContext);
  const mergeProps = {
    ...props
  };
  if (context.group) {
    mergeProps.checked = context.value === props.value;
    mergeProps.disabled = !!(context.disabled || props.disabled);
  }
  const {
    disabled,
    children,
    value,
    style,
    size,
    suffix,
    hint,
    theme,
    validationStatus,
    dataTestId,
    variant = 'default',
    onChange,
    name,
    ...rest
  } = props;
  const styles = composeThemeFromProps(defaultStyles, {
    theme
  }, {
    compose: Compose.Merge
  });
  const [_value, setValue] = useUncontrolled({
    value: mergeProps.checked,
    defaultValue: mergeProps.defaultChecked,
    fallbackValue: false,
    onChange
  });
  const mergedVariant = context.variant || variant;
  const mergedDisabled = context.disabled || disabled;
  const mergedSize = context.size || size;
  const {
    handleOnChange,
    onLabelClick
  } = useRadio({
    value,
    disabled: mergedDisabled,
    context,
    checked: _value,
    setValue,
    onChange,
    inputRef,
    onClick: props.onClick,
    children
  });
  const variantClassnames = clsx({
    [styles.default]: mergedVariant === 'default',
    [styles.boxed]: mergedVariant === 'boxed',
    [styles.button]: mergedVariant === 'button'
  });
  const statusClassnames = clsx({
    [styles.error]: validationStatus === 'error',
    [styles.success]: validationStatus === 'success',
    [styles.warning]: validationStatus === 'warning'
  });
  const sizeClassnames = clsx({
    [styles.sm]: mergedSize === 'sm',
    [styles.md]: mergedSize === 'md',
    [styles.lg]: mergedSize === 'lg'
  });
  const hintMessage = typeof hint === 'string' ? /*#__PURE__*/jsx(Typography, {
    variant: "caption",
    tag: "span",
    theme: {
      root: styles.hint
    },
    children: hint
  }) : hint;
  const renderRadio = context.variant !== 'button' ? /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsx("span", {
      title: name,
      className: clsx(styles.radio, styles.radio_hover, sizeClassnames, {
        [styles.radio_checked]: _value,
        [styles.no_hover]: mergedDisabled,
        [styles.radio_disabled]: mergedDisabled,
        [styles.radio_inactive]: !_value && mergedDisabled,
        [styles.radio_active]: _value && mergedDisabled
      }),
      onClick: onLabelClick,
      onKeyDown: event => {
        if (event.key === 'Enter' || event.key === 'Space') {
          event.persist();
          onLabelClick(event);
        }
      },
      onFocus: () => {
        wrapperRef.current && wrapperRef.current.setAttribute('data-focus', 'true');
      },
      onBlur: () => {
        wrapperRef.current && wrapperRef.current.setAttribute('data-focus', 'false');
      },
      role: "radio",
      "aria-checked": !!_value,
      tabIndex: 0
    }), (children || hint) && /*#__PURE__*/jsxs("span", {
      className: clsx(styles.content, {
        [styles.disabled]: mergedDisabled
      }),
      children: [children, hint && hintMessage, suffix && /*#__PURE__*/jsx("span", {
        className: styles.suffix,
        children: suffix
      })]
    })]
  }) : /*#__PURE__*/jsx("span", {
    className: styles.button_inner,
    children: children
  });
  return /*#__PURE__*/jsxs("label", {
    ref: wrapperRef,
    ...omit(rest, ['checked', 'onChange', 'theme', 'defaultChecked', 'theme']),
    style: style,
    tabIndex: -1,
    className: clsx(styles.root ?? '', variantClassnames, statusClassnames, {
      [styles.checked]: _value,
      [styles.disabled]: mergedDisabled
    }),
    htmlFor: name,
    children: [/*#__PURE__*/jsx("input", {
      ref: mergeRefs([inputRef, ref]),
      id: name,
      disabled: !!mergedDisabled,
      value: value || '',
      className: styles.input,
      type: "radio",
      tabIndex: -1,
      ...(context.name ? {
        name: context.name
      } : {
        name
      }),
      checked: !!_value,
      "data-test-id": dataTestId,
      onChange: event => {
        event.persist();
        handleOnChange(value, event);
      },
      onClick: e => {
        e.stopPropagation();
      }
    }), isFunction(children) ? children({
      checked: _value,
      disabled: !!mergedDisabled,
      name,
      onChange: (value, e) => handleOnChange(value, e),
      validationStatus
    }) : renderRadio]
  });
});
Radio.displayName = 'Radio';

export { Radio };
